import React from 'react';
import { create } from 'zustand';
import { apiFetch } from '@/lib/http/client';
import { invalidateEntitySurfaceCalendarEvents } from '@/lib/calendar/use-surface-calendar-event';
import { invalidateDocumentEvents } from '@/lib/calendar/use-document-events';
import { invalidateEntityAttendees } from '@/lib/recordings/use-entity-attendees';
import { localDateKey } from '@/lib/calendar/date-utils';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

export interface CalendarConnection {
  id: string;
  provider: 'google' | 'microsoft';
  email: string;
  displayName?: string;
  needsReauth: boolean;
  createdAt: string;
}

export interface CalendarSubscription {
  id: string;
  connectionId: string;
  externalId: string;
  name: string;
  color?: string;
  isSynced: boolean;
}

export interface CalendarAttendee {
  name?: string;
  email?: string;
  avatarUrl?: string;
}

export interface CalendarEvent {
  id: string;
  connectionId: string;
  subscriptionId: string;
  externalId: string;
  title: string;
  description?: string;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  location?: string;
  meetLink?: string;
  recurrenceRule?: string;
  organizerEmail?: string;
  organizerName?: string;
  attendees?: CalendarAttendee[];
  linkedDocumentIds: string[];
  /** Direct URL to open the event in the provider's calendar UI. Added when available from sync. */
  htmlLink?: string;
}

interface CalendarStoreState {
  connections: CalendarConnection[];
  subscriptions: Record<string, CalendarSubscription>; // keyed by subscription id
  events: Record<string, CalendarEvent[]>;  // key: "YYYY-MM-DD"
  selectedDate: string | null;              // "YYYY-MM-DD"
  selectedEvent: CalendarEvent | null;
  isPanelOpen: boolean;
  isLoading: boolean;
  error: string | null;
  fetchConnections: () => Promise<void>;
  fetchEvents: (start: Date, end: Date) => Promise<void>;
  selectDate: (date: string) => void;
  selectEvent: (event: CalendarEvent) => void;
  selectEventById: (eventId: string) => Promise<void>;
  closePanel: () => void;
  linkDocument: (eventId: string, documentId: string, surfaceId: string | null) => Promise<void>;
  unlinkDocument: (eventId: string, documentId: string, surfaceId: string | null) => Promise<void>;
  /** Remove a document from an event in EVERY scope. For the calendar panel,
   *  which lists documents rather than surfaces — "this document is no longer
   *  about this event at all". */
  unlinkDocumentEverywhere: (eventId: string, documentId: string) => Promise<void>;
  patchSubscription: (connectionId: string, subId: string, patch: { name?: string; color?: string; isSynced?: boolean }) => Promise<void>;
}

/** Returns all calendar events that link to the given document ID. */
export function useLinkedCalendarEvents(entityId: string | null | undefined): CalendarEvent[] {
  const events = useCalendarStore((s) => s.events);
  return React.useMemo(() => {
    if (!entityId) return [];
    return Object.values(events).flat().filter(e => e.linkedDocumentIds.includes(entityId));
  }, [events, entityId]);
}

// Generation counter for fetchConnections: bumped each time a new fetch starts.
// After each await, the stale-check `if (gen !== fetchGen) return` discards
// responses from superseded fetches (e.g. when the workspace switches mid-flight).
let fetchGen = 0;

export const useCalendarStore = create<CalendarStoreState>((set, get) => ({
  connections: [],
  subscriptions: {},
  events: {},
  selectedDate: null,
  selectedEvent: null,
  isPanelOpen: false,
  isLoading: false,
  error: null,

  fetchConnections: async () => {
    const gen = ++fetchGen;
    try {
      const connections = await apiFetch<CalendarConnection[]>('/workspace/integrations/calendar/connections');
      // Discard if a newer fetch has started (workspace switched mid-flight).
      if (gen !== fetchGen) return;
      set({ connections });
      // Fetch subscriptions for each connection
      const subsMap: Record<string, CalendarSubscription> = {};
      for (const conn of connections) {
        try {
          const subs = await apiFetch<CalendarSubscription[]>(`/workspace/integrations/calendar/connections/${conn.id}/subscriptions`);
          // Check again after each await — workspace may have switched during subscription fetches.
          if (gen !== fetchGen) return;
          for (const sub of subs) subsMap[sub.id] = sub;
        } catch { /* ignore individual failures */ }
      }
      if (gen !== fetchGen) return;
      set({ subscriptions: subsMap });
    } catch (e) {
      if (gen !== fetchGen) return;
      set({ error: e instanceof Error ? e.message : 'Failed to load connections' });
    }
  },

  fetchEvents: async (start: Date, end: Date) => {
    set({ isLoading: true, error: null });

    const monthKey = `calendar-${start.toISOString().slice(0, 7)}`; // e.g. "calendar-2026-04"

    // 1. Read IndexedDB cache first — instant render, works offline
    try {
      const { get: idbGet } = await import('idb-keyval');
      const cached = await idbGet<Record<string, CalendarEvent[]>>(monthKey);
      if (cached) set({ events: cached, isLoading: false });
    } catch {}

    // 2. Revalidate from API in background
    try {
      const startIso = start.toISOString();
      const endIso = end.toISOString();
      const events = await apiFetch<CalendarEvent[]>(
        `/workspace/integrations/calendar/events?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`
      );
      // Group events by date (YYYY-MM-DD)
      const byDate: Record<string, CalendarEvent[]> = {};
      for (const event of events) {
        const dateKey = localDateKey(new Date(event.startAt));
        if (!byDate[dateKey]) byDate[dateKey] = [];
        byDate[dateKey].push(event);
      }
      // If the selected event is open, refresh it with fresh data
      const { selectedEvent } = get();
      let updatedSelectedEvent = selectedEvent;
      if (selectedEvent) {
        const dateKey = localDateKey(new Date(selectedEvent.startAt));
        const freshEvent = byDate[dateKey]?.find(e => e.id === selectedEvent.id);
        updatedSelectedEvent = freshEvent ?? null;
      }
      set({
        events: byDate,
        isLoading: false,
        selectedEvent: updatedSelectedEvent,
        isPanelOpen: updatedSelectedEvent ? get().isPanelOpen : false,
      });

      // Write fresh data back to IndexedDB
      const { set: idbSet, del: idbDel, keys: idbKeys } = await import('idb-keyval');
      await idbSet(monthKey, byDate);

      // Prune keys older than 3 months
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - 3);
      const cutoffKey = `calendar-${cutoff.toISOString().slice(0, 7)}`;
      const allKeys = await idbKeys<string>();
      for (const key of allKeys) {
        if (typeof key === 'string' && key.startsWith('calendar-') && key < cutoffKey) {
          await idbDel(key);
        }
      }
    } catch (e) {
      // If API fails but cache was served, stay silent — user has offline data
      set((s) => ({
        isLoading: false,
        error: Object.keys(s.events).length > 0 ? null : e instanceof Error ? e.message : 'Failed to load events',
      }));
    }
  },

  selectDate: (date: string) => {
    set({ selectedDate: date });
  },

  selectEvent: (event: CalendarEvent) => {
    set({ selectedEvent: event, isPanelOpen: true });
  },

  selectEventById: async (eventId: string) => {
    // Check if already loaded in local state
    const allEvents = Object.values(get().events).flat();
    const cached = allEvents.find(e => e.id === eventId);
    if (cached) {
      set({ selectedEvent: cached, isPanelOpen: true });
      return;
    }
    // Fetch from API
    try {
      const event = await apiFetch<CalendarEvent>(
        `/workspace/integrations/calendar/events/${eventId}`,
      );
      set({ selectedEvent: event, isPanelOpen: true });
    } catch {
      // Event not found or no calendar connection — silently ignore
    }
  },

  closePanel: () => {
    set({ isPanelOpen: false, selectedEvent: null });
  },

  linkDocument: async (eventId: string, documentId: string, surfaceId: string | null) => {
    await apiFetch(
      `/workspace/integrations/calendar/events/${eventId}/documents`,
      { method: 'POST', body: JSON.stringify({ documentId, surfaceId }) }
    );
    // Add to linkedDocumentIds in cache
    const { events, selectedEvent } = get();
    const updatedEvents: Record<string, CalendarEvent[]> = {};
    for (const [date, dayEvents] of Object.entries(events)) {
      updatedEvents[date] = dayEvents.map(e =>
        e.id === eventId
          ? { ...e, linkedDocumentIds: [...e.linkedDocumentIds, documentId] }
          : e
      );
    }
    set({ events: updatedEvents });
    if (selectedEvent?.id === eventId) {
      set({ selectedEvent: { ...selectedEvent, linkedDocumentIds: [...selectedEvent.linkedDocumentIds, documentId] } });
    }
    invalidateDocumentEvents(documentId);
  },

  unlinkDocumentEverywhere: async (eventId: string, documentId: string) => {
    await apiFetch(
      `/workspace/integrations/calendar/events/${eventId}/documents/${documentId}?allSurfaces=true`,
      { method: 'DELETE' },
    );
    const { events, selectedEvent } = get();
    const updatedEvents: Record<string, CalendarEvent[]> = {};
    for (const [date, dayEvents] of Object.entries(events)) {
      updatedEvents[date] = dayEvents.map(e =>
        e.id === eventId
          ? { ...e, linkedDocumentIds: e.linkedDocumentIds.filter(id => id !== documentId) }
          : e
      );
    }
    set({ events: updatedEvents });
    if (selectedEvent?.id === eventId) {
      set({ selectedEvent: { ...selectedEvent, linkedDocumentIds: selectedEvent.linkedDocumentIds.filter(id => id !== documentId) } });
    }
    // The document's surfaces read their event and roster from caches of their
    // own, not from this store. Updating only `events` leaves every surface
    // rendering a meeting whose link this call just deleted. Entity-wide
    // because `allSurfaces=true` removed every one of them, and there is no
    // list here of which surfaces those were.
    invalidateEntitySurfaceCalendarEvents(documentId);
    invalidateEntityAttendees(documentId);
    invalidateDocumentEvents(documentId);
  },

  unlinkDocument: async (eventId: string, documentId: string, surfaceId: string | null) => {
    // Scoped: a document can hold one link per surface, so an unscoped DELETE
    // would remove every other surface's meeting too.
    const query = surfaceId ? `?surfaceId=${encodeURIComponent(surfaceId)}` : '';
    await apiFetch(
      `/workspace/integrations/calendar/events/${eventId}/documents/${documentId}${query}`,
      { method: 'DELETE' },
    );
    const { events, selectedEvent } = get();
    const updatedEvents: Record<string, CalendarEvent[]> = {};
    for (const [date, dayEvents] of Object.entries(events)) {
      updatedEvents[date] = dayEvents.map(e =>
        e.id === eventId
          ? { ...e, linkedDocumentIds: e.linkedDocumentIds.filter(id => id !== documentId) }
          : e
      );
    }
    set({ events: updatedEvents });
    if (selectedEvent?.id === eventId) {
      set({ selectedEvent: { ...selectedEvent, linkedDocumentIds: selectedEvent.linkedDocumentIds.filter(id => id !== documentId) } });
    }
    invalidateDocumentEvents(documentId);
  },

  patchSubscription: async (connectionId, subId, patch) => {
    const updated = await apiFetch<CalendarSubscription>(
      `/workspace/integrations/calendar/connections/${connectionId}/subscriptions/${subId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    );
    set(s => ({ subscriptions: { ...s.subscriptions, [subId]: updated } }));
  },
}));

// ── Workspace-switch reactivity ───────────────────────────────────────────────
//
// When the active workspace changes, the previously-loaded calendar data is no
// longer in scope. Clear cached state and (if a new workspace is active)
// re-fetch connections so the next render shows the new workspace's calendar.
//
// Subscribed at module load so the listener is registered before any UI mount.
// The subscription lives for the lifetime of the app.

// Note: Zustand `persist` rehydrates synchronously during create(), so by
// the time this module-body line runs, useWorkspaceStore.getState() returns
// the rehydrated activeWorkspaceId (not the initial null). The subscription
// below only fires on subsequent changes; the initial load is owned by
// component useEffect calls (CalendarPage, HomeCalendarWidget).
let previousActiveWorkspaceId: string | null =
  useWorkspaceStore.getState().activeWorkspaceId;

const unsubWorkspaceReactivity = useWorkspaceStore.subscribe((state) => {
  const next = state.activeWorkspaceId;
  if (next === previousActiveWorkspaceId) return;
  previousActiveWorkspaceId = next;

  // Always clear stale data — even on sign-out (next === null).
  useCalendarStore.setState({
    connections: [],
    subscriptions: {},
    events: {},
    selectedEvent: null,
    isPanelOpen: false,
  });

  // Only refetch if a workspace is now active. Sign-out clears state and stops.
  if (next !== null) {
    useCalendarStore.getState().fetchConnections();
  }
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => unsubWorkspaceReactivity());
}

/**
 * Test helper: re-syncs `previousActiveWorkspaceId` to the current
 * `useWorkspaceStore` value. Call this in `beforeEach` after resetting the
 * workspace store so each test starts from a clean subscription baseline.
 *
 * @internal — exported for tests only, named with leading underscore per
 * project convention for test-only exports.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function __resetCalendarStoreWorkspaceSubscription(): void {
  previousActiveWorkspaceId = useWorkspaceStore.getState().activeWorkspaceId;
}
